import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import CustomStepper from './FormComponents/CustomStepper';
import AnnonceContext from '../../../contexts/AnnonceContext';
import Page from 'src/components/Page';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { createAnnonce, updateAnnonceImage } from 'src/service/annonceApi';
import { notifySuccess, notifyErrorEmpty } from 'src/service/notificationService';
import { getCustomer } from 'src/service/customerApi';
import { toast } from 'react-toastify';
import AuthContext from 'src/contexts/AuthContext';

// Fonction utilitaire pour compresser une image
const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculer les nouvelles dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convertir en blob avec compression
      canvas.toBlob((blob) => {
        resolve(new File([blob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        }));
      }, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Fonction pour uploader une image avec retry
const uploadImageWithRetry = async (annonceId, imageFile, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Tentative d'upload ${attempt}/${maxRetries}`);
      
      // Compresser l'image avant upload
      const compressedImage = await compressImage(imageFile);
      
      const response = await updateAnnonceImage(annonceId, compressedImage);
      console.log('Upload réussi:', response);
      return response;
      
    } catch (error) {
      lastError = error;
      console.error(`Erreur upload tentative ${attempt}:`, error);
      
      if (attempt < maxRetries) {
        // Attendre avant de réessayer (backoff exponentiel)
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`Nouvelle tentative dans ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw new Error(`Échec de l'upload après ${maxRetries} tentatives: ${lastError?.message || 'Erreur inconnue'}`);
};

/*
    ville: 'Les Vieilles Charrues, Carhaix',
    placeDescription: 'Les Vieilles Charrues, Rue de Persivien, Carhaix-Plouguer, France',

    ville: 'Le Roi Arthur, Bréal-sous-Montfort',
    placeDescription: 'Le Roi Arthur, Le Mafeu, Bréal-sous-Montfort, France',

    ville: 'V and B Fest, Château-Gontier',
    placeDescription: 'V and B Fest, Château-Gontier, France',
*/

const Publier = () => {
  // Récupérer les données sauvegardées depuis localStorage
  const getSavedData = () => {
    try {
      const saved = localStorage.getItem('publier_form_data');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Erreur lors de la récupération des données sauvegardées:', error);
      return null;
    }
  };

  const savedData = getSavedData();
  
  const [activeStep, setActiveStep] = useState(savedData?.activeStep || 0);
  const [formAnnonce, setFormAnnonce] = useState(savedData?.formAnnonce || {
    origin: 'TROUVÉ',
    status: 'TROUVÉ',
    ville: '',
    placeDescription: '',
  });
  const [currentCategory, setCurrentCategory] = useState(savedData?.currentCategory || null);
  const [formAttributes, setFormAttributes] = useState(savedData?.formAttributes || []);
  const [imageUpload, setImageUpload] = useState(savedData?.imageUpload || null);
  const [date, setDate] = useState(savedData?.date || new Date().toISOString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const isSubmittingRef = useRef(false);
  const { customerId } = useContext(AuthContext);

  let navigate = useNavigate();

  useEffect(async () => {
    if (customerId) {
      await getCustomer(customerId).then(res => {
        setFormAnnonce({
          ...formAnnonce,
          origin: 'TROUVÉ',
          status: 'TROUVÉ',
          ville: res.name,
          placeDescription: res.name + ', France',
          professionalId: res.id,
          customerId: customerId,
          annonceAttributes: formAttributes,
        })
      })
    }
  }, [formAttributes, customerId]);

  // Sauvegarder les données quand elles changent
  useEffect(() => {
    // Ne pas sauvegarder si on est au step 0 et qu'il n'y a pas de données significatives
    const hasSignificantData = currentCategory || formAttributes.length > 0 || (imageUpload && imageUpload.data_url);
    
    if (activeStep === 0 && !hasSignificantData) {
      // Si on est au step 0 sans données, supprimer le localStorage
      clearSavedData();
      return;
    }
    
    const dataToSave = {
      activeStep,
      formAnnonce,
      currentCategory,
      formAttributes,
      date,
      // Sauvegarder imageUpload si c'est un objet avec data_url (pour la persistance)
      imageUpload: imageUpload && imageUpload.data_url ? imageUpload : null,
    };
    saveFormData(dataToSave);
  }, [activeStep, formAnnonce, currentCategory, formAttributes, date, imageUpload]);

  const checkStockage = () => {
    if (formAnnonce.stockage === undefined || formAnnonce.stockage === '')
      return false
    return true
  }

  // Fonction pour sauvegarder les données dans localStorage
  const saveFormData = (data) => {
    try {
      localStorage.setItem('publier_form_data', JSON.stringify(data));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des données:', error);
    }
  };

  // Fonction pour nettoyer les données sauvegardées
  const clearSavedData = () => {
    try {
      localStorage.removeItem('publier_form_data');
    } catch (error) {
      console.error('Erreur lors du nettoyage des données:', error);
    }
  };

  // Fonction pour réinitialiser complètement le formulaire
  const resetForm = () => {
    setActiveStep(0);
    setFormAnnonce({
      origin: 'TROUVÉ',
      status: 'TROUVÉ',
      ville: '',
      placeDescription: '',
    });
    setCurrentCategory(null);
    setFormAttributes([]);
    setImageUpload(null);
    setDate(new Date().toISOString());
    clearSavedData();
  };

  const handleNotifySuccess = () => {
    clearSavedData(); // Nettoyer les données après publication réussie
    // Remettre les états à false avant la navigation
    setIsSubmitting(false);
    isSubmittingRef.current = false;
    setUploadProgress(0);
    navigate('/dashboard/products/trouves');
  }

  const handleSubmit = async () => {
    if (isSubmitting || isSubmittingRef.current) {
      console.log('Soumission déjà en cours, ignoré');
      return; // Éviter les soumissions multiples
    }
    
    try {
      setIsSubmitting(true);
      isSubmittingRef.current = true;
      setUploadProgress(0);
      
      // Délai de protection pour éviter les doubles clics rapides
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!checkStockage()) {
        toast.error('Merci de renseigner un stockage', {
          position: 'top-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setIsSubmitting(false);
        isSubmittingRef.current = false;
        setUploadProgress(0);
        return;
      }
      
      // Étape 1: Créer l'annonce
      setUploadProgress(20);
      const response = await createAnnonce({
        ...formAnnonce,
        customerId: customerId
      });
      const annonceId = response.id;
      setUploadProgress(50);

      // Étape 2: Upload de l'image si présente
      const imageFile = imageUpload && imageUpload.file ? imageUpload.file : imageUpload;
      if (imageFile) {
        setUploadProgress(60);
        try {
          await uploadImageWithRetry(annonceId, imageFile);
          setUploadProgress(100);
        } catch (uploadError) {
          console.error('Erreur lors de l\'upload de l\'image:', uploadError);
          toast.warning('L\'annonce a été créée mais l\'image n\'a pas pu être uploadée. Vous pourrez la modifier plus tard.', {
            position: 'top-center',
            autoClose: 5000,
          });
        }
      } else {
        setUploadProgress(100);
      }
      
      // Ne pas remettre isSubmitting à false ici - laisser la navigation se faire
      // Le bouton restera désactivé jusqu'à ce que la page soit changée
      notifySuccess('Annonce ajoutée avec succès', handleNotifySuccess);
      
    } catch (error) {
      console.error('Erreur lors de la création de l\'annonce:', error);
      toast.error('Erreur lors de la création de l\'annonce. Veuillez réessayer.', {
        position: 'top-center',
        autoClose: 3000,
      });
      // Remettre à false seulement en cas d'erreur
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      setUploadProgress(0);
    }
  };

  return (
    <AnnonceContext.Provider
      value={{
        formAnnonce: formAnnonce,
        setFormAnnonce: setFormAnnonce,
        currentCategory: currentCategory,
        setCurrentCategory: setCurrentCategory,
        formAttributes: formAttributes,
        setFormAttributes: setFormAttributes,
        handleSubmit: handleSubmit,
        imageUpload: imageUpload,
        setImageUpload: setImageUpload,
        activeStep: activeStep,
        setActiveStep: setActiveStep,
        date: date,
        setDate: setDate,
        resetForm: resetForm,
        isSubmitting: isSubmitting,
        uploadProgress: uploadProgress,
      }}
    >
      <Page>
        <CustomStepper />
      </Page>
    </AnnonceContext.Provider>
  );
};

export default Publier;
